import {
  Col,
  Icon,
  Row,
  Text,
  Tooltip,
} from '@cubeyou-team/hx-react-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getItemIcon } from 'src/utils';
import { CAMPAIGN_STATUS } from 'src/modules/audience-activation/constants';
import {
  StyledRepositoryItem,
  StyledRepositoryItemClickArea,
  StyledRepositoryItemDraggable,
  StyledRepositoryItemStatus,
  StyledRepositoryItemStatusDots,
} from './StyledRepositoryItem';

import {
  DeleteCampaignModal,
  DraftBadge,
} from '../../modules/audience-activation';
import { getCurrentProductDataSources } from '../../selectors/myCompany';

import RepositoryItemActions from '../RepositoryItemActions';
import { useRepositoryItemContext } from './providers/RepositoryItemProvider';

const RepositoryItem = ({
  item,
  style,
  hideActions,
  actions,
  navigationDisabled,
  onFolderClick,
  onItemDragToggle,
}) => {
  const productDataSources = useSelector(state =>
    getCurrentProductDataSources(state)
  );
  const { showDeleteCampaignModal } = useRepositoryItemContext();
  const icon = getItemIcon(item, productDataSources);
  const { type, label, children, metadata, campaign, persistence } = item;
  const status = item.metadata && item.metadata.status;
  const isReady = !status || status === 'ready';
  const draggable = type === 'audience' && isReady;
  const loaded = typeof children === 'object';

  const [isMoreActionsOpen, SetIsMoreActionsOpen] = useState(false);
  const [isDragging, SetIsDragging] = useState(false);

  const statusLabel = useMemo(() => {
    const mapLabels = {
      ready: 'Ready',
      matching: 'Matching',
      error: 'Error',
    };
    return mapLabels[status] || 'None';
  }, [status]);

  useEffect(() => {
    if (showDeleteCampaignModal) SetIsMoreActionsOpen(false);
  }, [showDeleteCampaignModal]);

  return (
    <StyledRepositoryItem
      style={style}
      isDragging={isDragging}
      isTemporary={persistence === 'temporary'}
      disabled={navigationDisabled}
      data-test="item-row"
      onMouseLeave={() => SetIsMoreActionsOpen(false)}
    >
      <StyledRepositoryItemDraggable
        draggable={draggable}
        isDragging={isDragging}
        onDragStart={e => {
          onItemDragToggle(true);
          e.dataTransfer.setData('audience', JSON.stringify(item));
          SetIsDragging(true);
        }}
        onDragEnd={() => {
          onItemDragToggle(false);
          SetIsDragging(false);
        }}
      >
        <div>
          <Row>
            <Col span={12}>
              <StyledRepositoryItemClickArea
                onClick={() => {
                  if (type === 'folder' && !navigationDisabled) {
                    onFolderClick(item, loaded);
                  }
                }}
              >
                <Row valign="middle">
                  <Col span="auto">
                    <Icon size="small" icon={icon} variant={'dark'} />
                  </Col>
                  <Col
                    span={
                      metadata &&
                      ['conversation', 'tsv'].includes(
                        metadata.audience_type
                      ) &&
                      persistence === 'temporary'
                        ? 7
                        : 10
                    }
                  >
                    <Row valign="middle" halign="between">
                      <Text
                        size="small"
                        title={label}
                        style={{
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                        }}
                      >
                        {label}
                      </Text>
                      {campaign?.status === CAMPAIGN_STATUS.DRAFT && (
                        <DraftBadge />
                      )}
                    </Row>
                  </Col>
                  {metadata &&
                    persistence === 'temporary' &&
                    ['conversation', 'tsv'].includes(metadata.audience_type) &&
                    metadata.status !== 'matching' && (
                      <Col span="auto">
                        <Tooltip
                          content={statusLabel}
                          maxWidth="small"
                          position="top"
                          isInfoLabel
                        >
                          <StyledRepositoryItemStatus
                            status={metadata.status}
                          />
                        </Tooltip>
                      </Col>
                    )}
                  {metadata &&
                    persistence === 'temporary' &&
                    ['conversation', 'tsv'].includes(metadata.audience_type) &&
                    metadata.status === 'matching' && (
                      <Col span="auto">
                        <StyledRepositoryItemStatusDots>
                          <div className="dots" />
                        </StyledRepositoryItemStatusDots>
                      </Col>
                    )}
                </Row>
              </StyledRepositoryItemClickArea>
            </Col>
          </Row>
        </div>
      </StyledRepositoryItemDraggable>
      {!hideActions && (
        <RepositoryItemActions
          item={item}
          showMoreActions={isMoreActionsOpen}
          onMoreActionClick={SetIsMoreActionsOpen}
          actions={actions}
        />
      )}
      {item?.campaign && (
        <DeleteCampaignModal campaignId={item.campaign.campaignId} />
      )}
    </StyledRepositoryItem>
  );
};

RepositoryItem.defaultProps = {
  actions: {
    rename: () => {},
    delete: () => {},
    move_to: () => {},
    edit: () => {},
    preview: () => {},
    make_copy: () => {},
  },
};

export default RepositoryItem;
